import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { DemandStatusChipCell } from '@/legacy/components/DataGridCustom/cells/DemandStatusChipCell/DemandStatusChipCell';
import { NegotiationDemandDataGridColumnGroup, NegotiationDemandDataGridRow } from '~/common/types';
import {
  ChipCell,
  ChipInfoCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  LinkCell,
  LinkCellValue,
  PriorityCell,
  StatusChipCell,
  TooltipCell,
  UseDataGridConfig,
  UserSelectCell,
  cellFactory,
  dateFormatterFactory,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  joinFormatterFactory,
  DemandProcessStepsCell,
} from '~/components/DataGridCustom';
import { CuriosityLinkChipCell } from '~/components/DataGridCustom/cells/CuriosityLinkChip/CuriosityLinkChipCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { DemandProcessStatusChipCell } from '~/components/DataGridCustom/cells/DemandProcessStatusChipCell/DemandProcessStatusChipCell';
import {
  capabilitiesDetailsGetter,
  organizationValueGetter,
  supplierValueGetter,
} from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';

import { NegotiationDemandDataGridField } from './NegotiationDemandList.types';

export const useDemandDataGridConfig: UseDataGridConfig<NegotiationDemandDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'DemandDataGrid.column' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'DemandDataGrid.group' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: NegotiationDemandDataGridField.PRIORITY, sort: 'desc' }],
      columnVisibilityModel: {
        [NegotiationDemandDataGridField.PROCESSING_MODE]: false,
        [NegotiationDemandDataGridField.LATE_DELIVERY_AT]: false,
        [NegotiationDemandDataGridField.CASES_TOTAL_COUNT]: false,
        [NegotiationDemandDataGridField.CAPABILITY_IDS]: false,
        [NegotiationDemandDataGridField.STATUS]: false,
      },
      columns: [
        createColumn({
          field: NegotiationDemandDataGridField.PRIORITY,
          renderCell: cellFactory(PriorityCell, { needsPermission: Permission.DemandEditSetPriority }),
          minWidth: 100,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.demandDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 100,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.REFERENCE_ID,
          renderCell: cellFactory(LinkCell, { needs: { permission: Permission.BaseRequisitionList } }),
          valueGetter: (value, row): Union.Nullable<LinkCellValue> => {
            if (value) {
              return {
                to: row.purchaseRequisitionId ? RouteId.purchaseRequisitionDetail(row.purchaseRequisitionId) : '',
                label: value,
              };
            }
            return null;
          },
          valueFormatter: (value: { label: string }) => value?.label,
          sortable: false,
          minWidth: 100,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.PROCESS_STATUS,
          renderCell: cellFactory(DemandProcessStatusChipCell),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.PROCESS_STEPS,
          renderCell: cellFactory(DemandProcessStepsCell),
          minWidth: 400,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.PROCESSING_MODE,
          renderCell: cellFactory(StatusChipCell, ({ value }) => ({
            value: value ? t(`DemandProcessingMode.${value}`) : null,
          })),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.DESCRIPTION,
          renderCell: cellFactory(TooltipCell),
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.RESPONSIBLE_IDS,
          renderCell: cellFactory(UserSelectCell, ({ row }) => ({
            simplifyValue: true,
            capabilityIds: row.capabilityIds ?? [],
            commodityGroupIds: row.commodityGroupIds ?? [],
          })),
          valueGetter: (value) => value && value[0],
          sortable: false,
          minWidth: 300,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.PRIMARY_COMMODITY_GROUP,
          renderCell: cellFactory(CuriosityLinkChipCell),
          minWidth: 150,
          sortable: false,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.COMMODITY_GROUP_IDS,
          renderCell: cellFactory(CuriosityLinkChipCell),
          valueFormatter: joinFormatterFactory(),
          minWidth: 150,
          sortable: false,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.TOTAL_VALUE,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
        }),

        createColumn({
          field: NegotiationDemandDataGridField.REQUESTED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.REQUESTED_BY,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.INTERNAL_USER,
          }),
          minWidth: 150,
          sortable: false,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.ORGANIZATION_ID,
          renderCell: cellFactory(ChipCell, ({ row }) => ({ value: organizationValueGetter(row.organizationId) })),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.EARLY_DELIVERY_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.LATE_DELIVERY_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.PREFERRED_SUPPLIER_IDS,
          renderCell: cellFactory(LinkCell, ({ value }) => ({
            value: supplierValueGetter(value),
            needs: { permission: Permission.BaseSupplierList },
          })),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.CASES_TOTAL_COUNT,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.CASES_NEGOTIATED_COUNT,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.CASES_OPEN_COUNT,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.CAPABILITY_IDS,
          renderCell: cellFactory(ChipInfoCell, {
            detailsGetter: capabilitiesDetailsGetter(t),
          }),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          sortable: false,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationDemandDataGridField.STATUS,
          minWidth: 150,
          renderCell: cellFactory(DemandStatusChipCell),
        }),
        createColumn({
          field: NegotiationDemandDataGridField.SOURCE_SYSTEM_ID,
          renderCell: cellFactory(CuriosityValueCell, { ns: CuriosityNamespaceType.SOURCE_SYSTEM }),
          minWidth: 150,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: NegotiationDemandDataGridColumnGroup.DEMAND,
          renderHeaderGroup: headerGroupWithIcon(IconId.demand),
          children: [
            //
            { field: NegotiationDemandDataGridField.PRIORITY },
            { field: NegotiationDemandDataGridField.SEQUENCE_NUMBER },
            { field: NegotiationDemandDataGridField.PROCESS_STATUS },
            { field: NegotiationDemandDataGridField.PROCESS_STEPS },
            { field: NegotiationDemandDataGridField.PROCESSING_MODE },
            { field: NegotiationDemandDataGridField.DESCRIPTION },
            { field: NegotiationDemandDataGridField.PRIMARY_COMMODITY_GROUP },
            { field: NegotiationDemandDataGridField.COMMODITY_GROUP_IDS },
            { field: NegotiationDemandDataGridField.TOTAL_VALUE },
            { field: NegotiationDemandDataGridField.STATUS },
            { field: NegotiationDemandDataGridField.REFERENCE_ID },
            { field: NegotiationDemandDataGridField.LINKS },
            { field: NegotiationDemandDataGridField.CAPABILITY_IDS },
            { field: NegotiationDemandDataGridField.RESPONSIBLE_IDS },
            { field: NegotiationDemandDataGridField.SOURCE_SYSTEM_ID },
          ],
        }),
        createGroup({
          groupId: NegotiationDemandDataGridColumnGroup.REQUESTED,
          children: [
            //
            { field: NegotiationDemandDataGridField.REQUESTED_AT },
            { field: NegotiationDemandDataGridField.REQUESTED_BY },
            { field: NegotiationDemandDataGridField.ORGANIZATION_ID },
          ],
        }),
        createGroup({
          groupId: NegotiationDemandDataGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.HandshakeOutlined),
          children: [
            //
            { field: NegotiationDemandDataGridField.EARLY_DELIVERY_AT },
            { field: NegotiationDemandDataGridField.LATE_DELIVERY_AT },
          ],
        }),
        createGroup({
          groupId: NegotiationDemandDataGridColumnGroup.CASES,
          renderHeaderGroup: headerGroupWithIcon(IconId.negotiationCase),
          children: [
            //
            { field: NegotiationDemandDataGridField.PREFERRED_SUPPLIER_IDS },
            { field: NegotiationDemandDataGridField.CASES_TOTAL_COUNT },
            { field: NegotiationDemandDataGridField.CASES_NEGOTIATED_COUNT },
            { field: NegotiationDemandDataGridField.CASES_OPEN_COUNT },
          ],
        }),
      ],
      exportModel: {
        [NegotiationDemandDataGridField.LINKS]: {
          disable: true,
        },
        [NegotiationDemandDataGridField.COMMODITY_GROUP_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.COMMODITY_GROUP,
        },
        [NegotiationDemandDataGridField.ORGANIZATION_ID]: {
          resolveCuriosity: CuriosityNamespaceType.ORGANIZATION,
        },
        [NegotiationDemandDataGridField.CAPABILITY_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.CAPABILITY,
        },
        [NegotiationDemandDataGridField.PREFERRED_SUPPLIER_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.SUPPLIER,
        },
        [NegotiationDemandDataGridField.RESPONSIBLE_IDS]: {
          disable: true,
        },
        [NegotiationDemandDataGridField.REQUESTED_BY]: {
          resolveCuriosity: CuriosityNamespaceType.INTERNAL_USER,
        },
        [NegotiationDemandDataGridField.PROCESS_STEPS]: {
          disable: true,
        },
      },
    } as DataGridConfig<NegotiationDemandDataGridRow>;
  }, [t, tColumn, tGroup]);
};
